import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Object from "../../Images/OBJECTS.svg";
import Star from "../../Images/Star.svg";
import DoubleArrow from "../../Images/DoubleArrow.svg";
import FillStar from "../../Images/FillStar.svg";
import ToastMessage from '../../config/ToastMessage';
import { API_URL } from "../../config/api";

export default function WriteReview() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get data from navigation state
  const { courseId, enrollmentId, programName } = location.state || {};

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [title, setTitle] = useState('');
  const [review, setReview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });
  const [isValidData, setIsValidData] = useState(true);

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
  };

  const closeToast = () => {
    setToast({ show: false, type: '', message: '' });
  };

  // Check if required data is present
  useEffect(() => {
    if (!courseId || !enrollmentId) {
      setIsValidData(false);
      showToast('error', 'Missing course information. Redirecting...');
      const redirectTimer = setTimeout(() => {
        navigate('/accomplishment');
      }, 2000);

      return () => clearTimeout(redirectTimer);
    }
  }, []); // Empty dependency array - only run once on mount

  const handleSubmit = async () => {
    // Validation
    if (rating === 0) {
      showToast('error', 'Please select a rating');
      return;
    }

    if (!title.trim()) {
      showToast('error', 'Please enter a title for your review');
      return;
    }

    if (!courseId || !enrollmentId) {
      showToast('error', 'Missing required information');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        showToast('error', 'Please log in to submit a review');
        setIsSubmitting(false);
        return;
      }

      // FIXED: Changed endpoint from /course-reviews/create to /course_reviews/create
      const response = await fetch(`${API_URL}/course_reviews/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          enrollment_id: enrollmentId,
          course_id: courseId,
          rating: rating,
          review_title: title.trim(),
          review_text: review.trim() || null
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || result.message || 'Failed to submit review');
      }
      
      showToast('success', result.message || 'Review submitted successfully!');
      
      // Reset form after successful submission
      setTimeout(() => {
        setRating(0);
        setTitle('');
        setReview('');
        // Navigate back to accomplishments
        navigate('/accomplishment');
      }, 1500);

    } catch (err) {
      console.error('Review submission error:', err);
      showToast('error', err.message || 'An error occurred while submitting your review');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading/error state if data is invalid
  if (!isValidData) {
    return (
      <>
        {toast.show && (
          <ToastMessage
            type={toast.type}
            message={toast.message}
            onClose={closeToast}
          />
        )}
        <div className="min-h-screen p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Redirecting...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {toast.show && (
        <ToastMessage
          type={toast.type}
          message={toast.message}
          onClose={closeToast}
        />
      )}
      <div className="min-h-screen p-8">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6">
            <h1 
              className="text-xl font-bold text-zinc-800 cursor-pointer transition-colors hover:text-zinc-600"
              onClick={() => navigate('/accomplishment')}
            >
              Accomplishments
            </h1>
            <img src={DoubleArrow} alt="arrow" className="w-4 h-4" />
            <span className="text-base font-semibold text-zinc-800">Write Review</span>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-lg border border-white p-8 shadow-sm">
            {/* Program Header */}
            <div className="bg-gray-50 px-5 py-4 mb-8 -mx-8 -mt-8 rounded-t-lg">
              <h2 className="flex items-start text-xl font-semibold text-zinc-800">
                Program : {programName || 'Course Name'}
              </h2>
            </div>

            <div className="flex gap-12">
              {/* Left Side - Form */}
              <div className="w-[540px]">
                <h3 className="flex items-start text-2xl font-bold text-zinc-800 mb-6">Write Review</h3>

                {/* Rating */}
                <div className="mb-6">
                  <label className="flex items-start text-lg font-normal text-zinc-800 block mb-2">
                    Rating <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-3.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className="w-9 h-8 bg-white rounded-sm flex items-center justify-center transition-colors hover:bg-amber-50"
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        onClick={() => setRating(star)}
                        disabled={isSubmitting}
                      >
                        <img 
                          src={star <= (hoveredRating || rating) ? FillStar : Star}
                          alt="star" 
                          className="w-5 h-5"
                        />
                      </button>
                    ))}
                  </div>
                  {rating > 0 && (
                    <p className="text-sm text-zinc-600 mt-1">{rating} out of 5 stars</p>
                  )}
                </div>

                {/* Title Input */}
                <div className="mb-6">
                  <label className="flex items-start text-base font-normal text-zinc-800 block mb-2">
                    Title of Review <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Add a Title to Your Review"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={isSubmitting}
                    maxLength={100}
                    className="w-full h-11 px-3 py-2 bg-white rounded shadow-sm outline outline-1 outline-zinc-200 text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-2 focus:outline-[#4AACC5] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Review Textarea */}
                <div className="mb-6">
                  <label className="flex items-start text-base font-normal text-zinc-800 mb-2">
                    How was your overall experience
                  </label>
                  <textarea
                    placeholder="Write Text.."
                    value={review}
                    onChange={(e) => setReview(e.target.value)}
                    disabled={isSubmitting}
                    maxLength={1000}
                    className="w-full h-36 px-3 py-2 bg-white rounded shadow-sm outline outline-1 outline-zinc-200 text-sm text-zinc-800 placeholder:text-zinc-400 resize-none focus:outline-2 focus:outline-[#4AACC5] disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  {review.length > 0 && (
                    <p className="text-xs text-zinc-500 mt-1">{review.length}/1000 characters</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || rating === 0 || !title.trim()}
                  className="w-48 h-11 px-6 py-4 bg-[#4AACC5] rounded text-white text-base font-semibold hover:bg-[#7cbdce] transition-colors flex items-center justify-center disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Posting...
                    </>
                  ) : (
                    'Post'
                  )}
                </button>
              </div>

              {/* Right Side - Decorative Graphics */}
              <div className="flex-1 flex items-center justify-center relative">
                <img src={Object} alt="Decorative" className="w-80 h-80 object-contain" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}